import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { ADD_TRANSACTION, EDIT_TRANSACTION } from '../graphql/mutations/transactions';
import { CREATE_TOKEN_LOCATION, RENAME_TOKEN_LOCATION, DELETE_TOKEN_LOCATION } from '../graphql/mutations/tokenLocations';
import { GET_TOKEN_LOCATIONS } from '../graphql/queries/tokenLocations';
import { format } from 'date-fns';
import { addDecimalStrings, compareDecimalStrings, formatAmount, normalizeDecimalInput, parseFloatFromString, subtractDecimalStrings } from '../utils/formatters';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { SUPPORTED_CURRENCIES } from '../utils/currencies';
import { TransactionType } from '../utils/enums';
import { addTransactionToCache, updateTransactionInCache } from '../utils/apolloCache';
import toast from 'react-hot-toast';
import TokenLocationManageModal from './TokenLocationManageModal';

// CSS for improved date input in dark mode
const darkDateInputStyles = `
  /* Improve datetime input styling in dark mode */
  .dark .date-input::-webkit-calendar-picker-indicator {
    filter: invert(0.8);
  }
  
  /* Color adjustments for better dark mode visibility */
  .dark .date-input:focus {
    border-color: #4B5563;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }
`;

const userFriendlyTransactionError = (message, fallback = 'Could not save transaction') => {
  if (!message) return fallback;
  const raw = String(message);
  const lower = raw.toLowerCase();
  if (lower.includes('insufficient holdings') || lower.includes('insufficient source')) {
    return 'Not enough available balance for this transaction.';
  }
  if (lower.includes('invalid quantity') || lower.includes('invalid decimal string')) {
    return 'Invalid quantity. Use numbers only; comma thousands separators are accepted.';
  }
  if (lower.includes('invalid price')) {
    return 'Invalid price. Use numbers only; comma thousands separators are accepted.';
  }
  if (lower.includes('invalid fees') || lower.includes('invalid fee quantity')) {
    return 'Invalid fee. Use numbers only; comma thousands separators are accepted.';
  }
  return raw.split(':').pop().trim() || fallback;
};

const TransactionModal = ({ isOpen, onClose, tokenId, transaction, updateCache, portfolioId, refetch, holdingsAmount, currentPrice, tokenLocations = [], locationBalances = [], onSaved }) => {
  const isEditing = !!transaction;
  const { currentPortfolio } = usePortfolioContext();
  const client = useApolloClient();
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [originalEditQuantity, setOriginalEditQuantity] = useState(0);
  const [localTokenLocations, setLocalTokenLocations] = useState([]);
  const [locationManagerField, setLocationManagerField] = useState(null);
  const [formData, setFormData] = useState({
    transactionType: TransactionType.BUY,
    price: '',
    quantity: '',
    timestamp: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    fees: '',
    feeQuantity: '',
    cost: '',
    proceeds: '',
    notes: '',
    currency: '',
    locationId: '',
    sourceLocationId: '',
    destinationLocationId: '',
  });
  
  const [errors, setErrors] = useState({});

  const locationBalanceById = useMemo(() => {
    const byId = new Map();
    (locationBalances || []).forEach(balance => {
      if (!balance?.locationId) return;
      const quantity = parseFloatFromString(balance.quantity || '0');
      if (quantity > 0) byId.set(balance.locationId, quantity);
    });
    return byId;
  }, [locationBalances]);

  const unassignedQuantityString = subtractDecimalStrings(holdingsAmount || '0', locationBalances.map(balance => balance?.quantity || '0'));
  const unassignedQuantity = Math.max(0, parseFloatFromString(unassignedQuantityString));

  let largestBalanceLocationId = '';
  let largestLocationBalance = 0;
  locationBalanceById.forEach((quantity, locationId) => {
    if (quantity > largestLocationBalance) {
      largestLocationBalance = quantity;
      largestBalanceLocationId = locationId;
    }
  });
  const defaultLocationId = largestLocationBalance >= unassignedQuantity ? largestBalanceLocationId : '';

  // Read through a ref so balance refetches while the modal is open don't reset the form.
  const defaultLocationsRef = useRef({ locationId: '', sourceLocationId: '' });
  defaultLocationsRef.current = { locationId: defaultLocationId, sourceLocationId: largestBalanceLocationId };
  
  // Helper function to calculate and balance fields
  const calculateFields = (data, changedField) => {
    // Create a copy to work with
    const result = { ...data };
    
    // Skip calculations for transfer transactions
    if (TransactionType.isTransfer(result.transactionType)) {
      return result;
    }
    
    // Determine if we're working with a sell/transfer-out transaction
    const isSellOrTransferOut = result.transactionType === TransactionType.SELL || 
                                result.transactionType === TransactionType.TRANSFER_OUT;
    
    // Get numeric values for calculations
    const valueField = isSellOrTransferOut ? 'proceeds' : 'cost';
    
    // Check if the changed field was cleared (empty string)
    const wasCleared = changedField && result[changedField] === '';
    
    // If a field was cleared, don't recalculate other fields
    if (wasCleared) {
      return result;
    }
    
    const values = {
      price: parseFloatFromString(result.price) || 0,
      quantity: parseFloatFromString(result.quantity) || 0,
      value: parseFloatFromString(result[valueField]) || 0
    };
    
    // Check if all fields have values
    const allFieldsHaveValues = values.price > 0 && values.quantity > 0 && values.value > 0;
    
    // Check if only price is not set (quantity and cost/proceeds are set)
    const onlyPriceNotSet = values.price === 0 && values.quantity > 0 && values.value > 0;
    
    // Calculate based on which field was changed
    if (changedField === 'price') {
      if (allFieldsHaveValues || (values.quantity > 0 && values.price > 0)) {
        // Recalculate cost/proceeds when price changes
        values.value = values.price * values.quantity;
        result[valueField] = values.value.toString();
      }
    } 
    else if (changedField === 'quantity') {
      if (allFieldsHaveValues) {
        // When all fields have values and quantity changes, recalculate price
        values.price = values.value / values.quantity;
        result.price = values.price.toString();
      }
      else if (values.price > 0 && values.quantity > 0) {
        // If not all fields set but price exists, calculate cost/proceeds
        values.value = values.price * values.quantity;
        result[valueField] = values.value.toString();
      }
      else if (onlyPriceNotSet) {
        // When only price is not set, calculate it
        values.price = values.value / values.quantity;
        result.price = values.price.toString();
      }
    }
    else if (changedField === valueField) {
      if (allFieldsHaveValues || (values.quantity > 0 && values.value > 0)) {
        // When cost/proceeds changes, recalculate price
        values.price = values.value / values.quantity;
        result.price = values.price.toString();
      }
    }
    // If no field was specified (form submission), then calculate any missing field
    else if (!changedField) {
      if (values.price > 0 && values.quantity > 0 && values.value === 0) {
        // Calculate value from price and quantity
        values.value = values.price * values.quantity;
        result[valueField] = values.value.toString();
      }
      else if (values.price > 0 && values.value > 0 && values.quantity === 0) {
        // Calculate quantity from value and price - ONLY during form submission
        values.quantity = values.value / values.price;
        result.quantity = values.quantity.toString();
      }
      else if (values.quantity > 0 && values.value > 0 && values.price === 0) {
        // Calculate price from value and quantity
        values.price = values.value / values.quantity;
        result.price = values.price.toString();
      }
    }
    
    return result;
  };
  
  // Focus on first input when modal opens
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      // Short timeout to ensure DOM is fully rendered
      setTimeout(() => {
        firstInputRef.current.focus();
      }, 50);
    }
  }, [isOpen]);
  
  // Add event listener for ESC key press
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (locationManagerField) {
          setLocationManagerField(null);
          return;
        }
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose, locationManagerField]);
  
  // Backdrop click handler removed to prevent accidental closure
  
  // Reset form when modal opens or transaction changes
  useEffect(() => {
    if (isOpen) {
      if (isEditing && transaction) {
        // Format the date for the input field
        const timestamp = new Date(transaction.timestamp);
        const formattedTimestamp = format(timestamp, "yyyy-MM-dd'T'HH:mm");
        
        // Normalize transaction type
        const txType = transaction.transactionType?.toUpperCase?.() || transaction.transactionType;
        
        setFormData({
          transactionType: txType,
          price: parseFloatFromString(transaction.price) || '',
          quantity: parseFloatFromString(transaction.quantity) || '',
          timestamp: formattedTimestamp,
          fees: parseFloatFromString(transaction.fees) || '',
          feeQuantity: parseFloatFromString(transaction.feeQuantity) || '',
          cost: parseFloatFromString(transaction.cost) || '',
          proceeds: parseFloatFromString(transaction.proceeds) || '',
          notes: transaction.notes || '',
          currency: transaction.currency || (currentPortfolio?.currency || 'USD'),
          locationId: transaction.locationId || '',
          sourceLocationId: transaction.sourceLocationId || '',
          destinationLocationId: transaction.destinationLocationId || '',
        });
        setOriginalEditQuantity(parseFloatFromString(transaction.quantity) || 0);
      } else {
        // Reset form for new transaction
        setFormData({
          transactionType: TransactionType.BUY,
          price: '',
          quantity: '',
          timestamp: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
          fees: '',
          feeQuantity: '',
          cost: '',
          proceeds: '',
          notes: '',
          currency: currentPortfolio?.currency || 'USD',
          locationId: defaultLocationsRef.current.locationId,
          sourceLocationId: '',
          destinationLocationId: '',
        });
        setOriginalEditQuantity(0);
      }
      setErrors({});
      setLocationManagerField(null);
    }
  }, [isOpen, transaction, isEditing, currentPortfolio?.currency]);

  useEffect(() => {
    setLocalTokenLocations([]);
  }, [portfolioId]);

  // Define mutations
  const [addTransaction, { loading: addLoading }] = useMutation(ADD_TRANSACTION);
  const [editTransaction, { loading: editLoading }] = useMutation(EDIT_TRANSACTION);
  const [createTokenLocation] = useMutation(CREATE_TOKEN_LOCATION);
  const [renameTokenLocation] = useMutation(RENAME_TOKEN_LOCATION);
  const [deleteTokenLocation] = useMutation(DELETE_TOKEN_LOCATION);
  
  const loading = addLoading || editLoading;

  const availableTokenLocations = useMemo(() => {
    const byId = new Map();
    [...(tokenLocations || []), ...(localTokenLocations || [])].forEach(location => {
      if (location?.id) byId.set(location.id, location);
    });
    return [...byId.values()].sort((a, b) => {
      const balanceDiff = (locationBalanceById.get(b.id) || 0) - (locationBalanceById.get(a.id) || 0);
      if (balanceDiff !== 0) return balanceDiff;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [tokenLocations, localTokenLocations, locationBalanceById]);

  const locationOptionLabel = (location) => {
    const balance = locationBalanceById.get(location.id);
    return balance ? `${location.name} (${formatAmount(balance)})` : location.name;
  };

  // Check if the transaction type is transfer in/out
  const isTransfer = TransactionType.isTransfer(formData.transactionType);
  const isInternalTransfer = formData.transactionType === TransactionType.INTERNAL_TRANSFER;
  const isSellOrTransferOut = formData.transactionType === TransactionType.SELL || formData.transactionType === TransactionType.TRANSFER_OUT;
  const hasLocations = availableTokenLocations.length > 0;
  const toOptionalId = (value) => (value && value !== '' ? value : undefined);

  const selectedSourceBalanceString = isInternalTransfer && formData.sourceLocationId
    ? (locationBalances.find(balance => balance?.locationId === formData.sourceLocationId)?.quantity || '0')
    : '0';
  const originalInternalTransferDebitString = isInternalTransfer && isEditing && transaction?.sourceLocationId === formData.sourceLocationId
    ? addDecimalStrings([transaction.quantity || '0', transaction.feeQuantity || '0'])
    : '0';
  const availableSourceBalanceString = addDecimalStrings([selectedSourceBalanceString, originalInternalTransferDebitString]);
  const tokenFeeQuantityString = formData.feeQuantity || '0';
  const internalTransferMaxQuantityString = subtractDecimalStrings(availableSourceBalanceString, [tokenFeeQuantityString]);

  const computedMaxQuantityString = isSellOrTransferOut
    ? (isEditing ? addDecimalStrings([holdingsAmount || '0', originalEditQuantity.toString()]) : (holdingsAmount || '0'))
    : (isInternalTransfer && compareDecimalStrings(internalTransferMaxQuantityString, '0') > 0 ? internalTransferMaxQuantityString : '0');
  const showMaxQuantityButton = (isSellOrTransferOut || isInternalTransfer) && compareDecimalStrings(computedMaxQuantityString, '0') > 0;

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Create a copy of current form data to work with
    const updatedFormData = { ...formData };
    
    // Update the changed field
    updatedFormData[name] = value;
    
    // Calculate related fields
    const recalculatedData = calculateFields(updatedFormData, name);
    
    // Update state
    setFormData(recalculatedData);

    // Clear error for this field when user makes changes
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const openLocationManager = (fieldName) => {
    if (!portfolioId) return;
    setLocationManagerField(fieldName);
  };

  const closeLocationManager = () => setLocationManagerField(null);

  const refreshTokenLocations = async () => {
    const result = await client.query({
      query: GET_TOKEN_LOCATIONS,
      variables: { portfolioId },
      fetchPolicy: 'network-only',
    });
    const updatedLocations = result?.data?.tokenLocations || [];
    setLocalTokenLocations(updatedLocations);
    return updatedLocations;
  };

  const selectedLocationManagerId = locationManagerField ? (formData[locationManagerField] || '') : '';

  const handleManagedLocationSelect = (locationId, selectedLocation = null) => {
    if (!locationManagerField) return;
    setFormData(current => ({ ...current, [locationManagerField]: locationId || '' }));
    setErrors(current => ({ ...current, [locationManagerField]: null }));
    if (selectedLocation?.id) {
      setLocalTokenLocations(current => {
        const byId = new Map([...(current || []), selectedLocation].map(location => [location.id, location]));
        return [...byId.values()];
      });
    }
  };

  const handleManagedLocationCreate = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || !portfolioId) return null;

    const exactExisting = availableTokenLocations.find(location => (location.name || '').toLowerCase() === trimmed.toLowerCase());
    if (exactExisting?.id) {
      handleManagedLocationSelect(exactExisting.id, exactExisting);
      return exactExisting;
    }

    try {
      await createTokenLocation({
        variables: { portfolioId, name: trimmed },
        refetchQueries: [{ query: GET_TOKEN_LOCATIONS, variables: { portfolioId } }],
        awaitRefetchQueries: true,
      });
      const updatedLocations = await refreshTokenLocations();
      const created = updatedLocations.find(location => (location.name || '').toLowerCase() === trimmed.toLowerCase());
      if (created?.id) {
        handleManagedLocationSelect(created.id, created);
        toast.success(`Created location ${created.name}`);
      } else {
        toast.success('Created location');
      }
      try { await refetch?.(); } catch (_) {}
      return created || null;
    } catch (error) {
      console.error('Error creating location:', error);
      toast.error(error?.message || 'Failed to create location');
      return null;
    }
  };

  const handleManagedLocationRename = async (locationId, name) => {
    const trimmed = (name || '').trim();
    if (!locationId || !trimmed || !portfolioId) return false;
    try {
      await renameTokenLocation({
        variables: { portfolioId, locationId, name: trimmed },
        refetchQueries: [{ query: GET_TOKEN_LOCATIONS, variables: { portfolioId } }],
        awaitRefetchQueries: true,
      });
      await refreshTokenLocations();
      toast.success('Location renamed');
      try { await refetch?.(); } catch (_) {}
      return true;
    } catch (error) {
      console.error('Error renaming location:', error);
      toast.error(error?.message || 'Failed to rename location');
      return false;
    }
  };

  const locationDeleteErrorMessage = (error) => {
    const raw = String(error?.message || '');
    const lower = raw.toLowerCase();
    if (lower.includes('assigned') || lower.includes('transaction') || lower.includes('balance') || lower.includes('internal error')) {
      return 'Cannot delete location while transactions or balances use it.';
    }
    return raw || 'Failed to delete location';
  };

  const handleManagedLocationDelete = async (location) => {
    if (!location?.id || !portfolioId) return false;
    try {
      const result = await deleteTokenLocation({
        variables: { portfolioId, locationId: location.id },
        refetchQueries: [{ query: GET_TOKEN_LOCATIONS, variables: { portfolioId } }],
        awaitRefetchQueries: true,
      });
      const deleted = result?.data?.deleteTokenLocation;
      if (deleted !== true) {
        throw new Error('Location was not deleted');
      }
      await refreshTokenLocations();
      setFormData(current => ({
        ...current,
        locationId: current.locationId === location.id ? '' : current.locationId,
        sourceLocationId: current.sourceLocationId === location.id ? '' : current.sourceLocationId,
        destinationLocationId: current.destinationLocationId === location.id ? '' : current.destinationLocationId,
      }));
      toast.success(`Deleted location ${location.name || ''}`.trim());
      try { await refetch?.(); } catch (_) {}
      return true;
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(locationDeleteErrorMessage(error));
      return false;
    }
  };

  const renderLocationLabel = (label, fieldName) => (
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{label}</span>
      <button
        type="button"
        onClick={() => openLocationManager(fieldName)}
        disabled={loading || !portfolioId}
        className="text-xs font-medium text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-blue-400"
      >
        edit
      </button>
    </div>
  );

  const handleTypeSelect = (type) => {
    if (loading) return;
    const toInternal = type === TransactionType.INTERNAL_TRANSFER;
    const wasInternal = formData.transactionType === TransactionType.INTERNAL_TRANSFER;
    let locationId = toInternal ? '' : formData.locationId;
    let sourceLocationId = toInternal ? formData.sourceLocationId : '';
    if (!isEditing && toInternal && !wasInternal) {
      sourceLocationId = sourceLocationId || formData.locationId || largestBalanceLocationId;
    }
    if (!isEditing && !toInternal && wasInternal) {
      locationId = locationId || defaultLocationId;
    }
    const updated = {
      ...formData,
      transactionType: type,
      locationId,
      sourceLocationId,
      destinationLocationId: type === TransactionType.INTERNAL_TRANSFER ? formData.destinationLocationId : '',
    };
    const recalculated = calculateFields(updated, 'transactionType');
    setFormData(recalculated);
  };

  const handleMaxQuantity = () => {
    if (!showMaxQuantityButton) return;
    const maxQuantity = computedMaxQuantityString;
    if (compareDecimalStrings(maxQuantity, '0') <= 0) return;
    const updated = { ...formData, quantity: maxQuantity };
    const recalculated = calculateFields(updated, 'quantity');
    setFormData(recalculated);
    if (errors.quantity) {
      setErrors({ ...errors, quantity: null });
    }
  };

  const handleUseCurrentPrice = () => {
    if (!currentPrice) return;
    const numericPrice = parseFloatFromString(currentPrice);
    if (numericPrice <= 0) return;
    const updated = { ...formData, price: numericPrice.toString() };
    const recalculated = calculateFields(updated, 'price');
    setFormData(recalculated);
    if (errors.price) {
      setErrors({ ...errors, price: null });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Auto-calculate any missing field before submission
    const submitData = calculateFields({ ...formData });
    
    // Convert to numeric values for validation
    const numericValues = {
      price: parseFloatFromString(submitData.price) || 0,
      quantity: parseFloatFromString(submitData.quantity) || 0,
      feeQuantity: parseFloatFromString(submitData.feeQuantity) || 0
    };
    
    // Debug log to see exact values
    console.log('Submitting with values:', {
      tokenId,
      price: numericValues.price,
      priceType: typeof numericValues.price,
      quantity: numericValues.quantity,
      rawPrice: submitData.price,
      transactionType: submitData.transactionType,
      isTransfer
    });
    
    // Validate tokenId
    if (!isEditing && (!tokenId || tokenId === '')) {
      setErrors({ submit: 'Missing tokenId. Please try again or contact support.' });
      console.error('Missing required tokenId for new transaction');
      return;
    }
    
    // Validate form
    const newErrors = {};
    if (numericValues.quantity <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }
    
    // For non-transfer transactions, price must be greater than 0
    // Use a very small epsilon to handle potential floating-point precision issues
    const EPSILON = 1e-10;
    if (!isTransfer && numericValues.price < EPSILON) {
      newErrors.price = 'Price must be greater than 0';
    }
    if (isInternalTransfer) {
      if (!submitData.sourceLocationId) {
        newErrors.sourceLocationId = 'Source location is required';
      }
      if (!submitData.destinationLocationId) {
        newErrors.destinationLocationId = 'Destination location is required';
      }
      if (submitData.sourceLocationId && submitData.destinationLocationId && submitData.sourceLocationId === submitData.destinationLocationId) {
        newErrors.destinationLocationId = 'Destination must be different from source';
      }
    }
    if (isSellOrTransferOut && hasLocations && !submitData.locationId && unassignedQuantity <= 0) {
      newErrors.locationId = 'Pick the location to sell/transfer from. There is no unassigned balance to consume.';
    }
    
    if (isInternalTransfer) {
      if (numericValues.feeQuantity < 0) {
        newErrors.feeQuantity = 'Token fee cannot be negative';
      }
      const totalSourceDebitString = addDecimalStrings([submitData.quantity || '0', submitData.feeQuantity || '0']);
      if (submitData.sourceLocationId && compareDecimalStrings(totalSourceDebitString, availableSourceBalanceString) > 0) {
        newErrors.quantity = `Quantity plus token fee cannot exceed source balance (${availableSourceBalanceString})`;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (isEditing) {
        // Update existing transaction
        const variables = {
          transactionId: transaction.id,
          portfolioId: portfolioId,
          transactionType: submitData.transactionType,
          timestamp: new Date(submitData.timestamp).toISOString(),
          notes: submitData.notes || '',
        };
        // For transfers, explicitly send price 0; otherwise send provided price if any
        if (isTransfer) {
          variables.price = '0';
        } else if (submitData.price && submitData.price !== '') {
          variables.price = normalizeDecimalInput(submitData.price);
        }
        if (submitData.quantity && submitData.quantity !== '') {
          variables.quantity = normalizeDecimalInput(submitData.quantity);
        }
        if (submitData.fees && submitData.fees !== '') {
          variables.fees = normalizeDecimalInput(submitData.fees);
        }
        if (isInternalTransfer) {
          variables.feeQuantity = submitData.feeQuantity && submitData.feeQuantity !== '' ? normalizeDecimalInput(submitData.feeQuantity) : '0';
        }
        if (submitData.currency && submitData.currency !== '') {
          variables.currency = submitData.currency;
        }
        variables.locationId = isInternalTransfer ? '' : (submitData.locationId || '');
        variables.sourceLocationId = isInternalTransfer ? (submitData.sourceLocationId || '') : '';
        variables.destinationLocationId = isInternalTransfer ? (submitData.destinationLocationId || '') : '';
        const response = await editTransaction({
          variables,
          update: (cache, { data }) => {
            // Update the cache using Apollo's update function for better error handling
            if (data?.editTransaction?.success && data?.editTransaction?.transaction) {
              updateCache((cache, variables) => {
                updateTransactionInCache(cache, variables, transaction.id, data.editTransaction.transaction);
              });
            }
          },
        });
        
        // Check for errors
        if (!response.data?.editTransaction?.success) {
          const rawErrorMessage = response.data?.editTransaction?.error || 'Failed to update transaction';
          const errorMessage = userFriendlyTransactionError(rawErrorMessage, 'Failed to update transaction');
          console.error('Edit transaction failed:', rawErrorMessage);
          setErrors({ submit: errorMessage });
          return;
        }
        
        // Show success toast
        toast.success('Transaction updated successfully');

        // Notify parent for immediate UI update
        try {
          const updatedTx = response?.data?.editTransaction?.transaction;
          if (updatedTx && typeof onSaved === 'function') {
            onSaved(updatedTx);
          }
        } catch (_) {}

        // Ensure server-calculated aggregates are refreshed in background
        if (typeof refetch === 'function') {
          try {
            refetch();
          } catch (_) {}
        }
      } else {
        // Create new transaction
        console.log('TransactionModal - portfolioId:', portfolioId, 'tokenId:', tokenId);
        
        if (!portfolioId) {
          throw new Error('No portfolio selected. Please refresh the page and try again.');
        }
        const variables = {
          tokenId,
          portfolioId,
          transactionType: submitData.transactionType,
          // For transfers, API still requires price - send '0'
          price: isTransfer ? '0' : normalizeDecimalInput(submitData.price?.toString() ?? '0'),
          quantity: normalizeDecimalInput(submitData.quantity?.toString() ?? ''),
          timestamp: new Date(submitData.timestamp).toISOString(),
          notes: submitData.notes || '',
        };
        if (submitData.fees && submitData.fees !== '') {
          variables.fees = normalizeDecimalInput(submitData.fees);
        }
        if (isInternalTransfer) {
          variables.feeQuantity = submitData.feeQuantity && submitData.feeQuantity !== '' ? normalizeDecimalInput(submitData.feeQuantity) : '0';
        }
        variables.currency = submitData.currency || (currentPortfolio?.currency || 'USD');
        variables.locationId = isInternalTransfer ? undefined : toOptionalId(submitData.locationId);
        variables.sourceLocationId = isInternalTransfer ? toOptionalId(submitData.sourceLocationId) : undefined;
        variables.destinationLocationId = isInternalTransfer ? toOptionalId(submitData.destinationLocationId) : undefined;
        const response = await addTransaction({
          variables,
          update: (cache, { data }) => {
            // Update the cache using Apollo's update function for better error handling
            if (data?.addTransaction?.success && data?.addTransaction?.transaction) {
              updateCache((cache, variables) => {
                addTransactionToCache(cache, variables, data.addTransaction.transaction);
              });
            }
          },
        });
        
        // Check for errors
        if (!response.data?.addTransaction?.success) {
          const rawErrorMessage = response.data?.addTransaction?.error || 'Failed to add transaction';
          const errorMessage = userFriendlyTransactionError(rawErrorMessage, 'Failed to add transaction');
          console.error('Add transaction failed:', rawErrorMessage);
          setErrors({ submit: errorMessage });
          return;
        }
        
        // Show success toast
        toast.success('Transaction added successfully');

        // Notify parent for immediate UI update (optional)
        try {
          const newTx = response?.data?.addTransaction?.transaction;
          if (newTx && typeof onSaved === 'function') {
            onSaved(newTx);
          }
        } catch (_) {}

        // If this was the first transaction (position may not exist yet), trigger a background refetch
        if (typeof refetch === 'function') {
          try {
            refetch();
          } catch (_) {
            // ignore
          }
        }
      }
      
      // Close modal - refetch (if triggered above) runs in background
      onClose();
    } catch (error) {
      console.error('Error saving transaction:', error);
      setErrors({ submit: userFriendlyTransactionError(error.message) });
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center"
    >
      {/* Inject custom styles */}
      <style>{darkDateInputStyles}</style>
      
      <div 
        ref={modalRef}
        className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside the modal from closing it
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Transaction' : 'Add Transaction'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <TokenLocationManageModal
          isOpen={!!locationManagerField}
          onClose={closeLocationManager}
          locations={availableTokenLocations}
          selectedLocationId={selectedLocationManagerId}
          onSelect={handleManagedLocationSelect}
          onCreate={handleManagedLocationCreate}
          onRename={handleManagedLocationRename}
          onDelete={handleManagedLocationDelete}
          tokenLocationBalances={locationBalances}
        />

        <form onSubmit={handleSubmit}>
          {/* Transaction Type */}
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              Transaction Type
            </label>
            <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-x-auto max-w-full" role="group">
              {[
                { label: 'Buy', value: TransactionType.BUY },
                { label: 'Sell', value: TransactionType.SELL },
                { label: 'Transfer In', value: TransactionType.TRANSFER_IN },
                { label: 'Transfer Out', value: TransactionType.TRANSFER_OUT },
                { label: 'Internal', value: TransactionType.INTERNAL_TRANSFER },
              ].map((opt, idx) => {
                const isActive = formData.transactionType === opt.value;
                return (
                  <button
                    key={opt.value}
                    ref={idx === 0 ? firstInputRef : null}
                    type="button"
                    onClick={() => handleTypeSelect(opt.value)}
                    disabled={loading}
                    className={`text-xs sm:text-sm px-3 py-1.5 whitespace-nowrap focus:outline-none transition-colors ${
                      isActive
                        ? 'bg-blue-500 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    } ${idx !== 0 ? 'border-l border-gray-300 dark:border-gray-600' : ''}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {!isInternalTransfer && (
            <div className="mb-4">
              {renderLocationLabel('Location', 'locationId')}
              <select
                name="locationId"
                value={formData.locationId}
                onChange={handleChange}
                className={`shadow appearance-none border ${errors.locationId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline`}
                disabled={loading}
              >
                <option value="">Unassigned{unassignedQuantity > 0 ? ` (${formatAmount(unassignedQuantity)})` : ''}</option>
                {availableTokenLocations.map(location => (
                  <option key={location.id} value={location.id}>{locationOptionLabel(location)}</option>
                ))}
              </select>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {isSellOrTransferOut ? 'Select a location unless you are consuming unassigned balance.' : 'Optional custody/account label for this transaction.'}
              </div>
              {errors.locationId && <p className="text-red-500 text-xs italic">{errors.locationId}</p>}
            </div>
          )}

          {isInternalTransfer && (
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                {renderLocationLabel('From location', 'sourceLocationId')}
                <select
                  name="sourceLocationId"
                  value={formData.sourceLocationId}
                  onChange={handleChange}
                  className={`shadow appearance-none border ${errors.sourceLocationId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline`}
                  disabled={loading || !hasLocations}
                >
                  <option value="">Select source</option>
                  {availableTokenLocations.map(location => (
                    <option key={location.id} value={location.id}>{locationOptionLabel(location)}</option>
                  ))}
                </select>
                {errors.sourceLocationId && <p className="text-red-500 text-xs italic">{errors.sourceLocationId}</p>}
              </div>
              <div>
                {renderLocationLabel('To location', 'destinationLocationId')}
                <select
                  name="destinationLocationId"
                  value={formData.destinationLocationId}
                  onChange={handleChange}
                  className={`shadow appearance-none border ${errors.destinationLocationId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline`}
                  disabled={loading || !hasLocations}
                >
                  <option value="">Select destination</option>
                  {availableTokenLocations.map(location => (
                    <option key={location.id} value={location.id}>{locationOptionLabel(location)}</option>
                  ))}
                </select>
                {errors.destinationLocationId && <p className="text-red-500 text-xs italic">{errors.destinationLocationId}</p>}
              </div>
              {!hasLocations && (
                <p className="sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">
                  Create token locations first, then use internal transfers to move quantity between them.
                </p>
              )}
            </div>
          )}

          {/* Cost (for BUY and TRANSFER_IN only) */}
          {(formData.transactionType === TransactionType.BUY) && (
            <div className="mb-4">
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Cost
              </label>
              <div className="flex items-end gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  name="cost"
                  value={formData.cost}
                  onChange={handleChange}
                  className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded flex-1 py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  disabled={loading}
                  placeholder="0.00"
                />
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-20 py-2 px-2 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline text-sm"
                  disabled={loading}
                  aria-label="Currency"
                >
                  {SUPPORTED_CURRENCIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Formula: Cost = Quantity × Price
              </div>
            </div>
          )}

          {/* Proceeds (for SELL and TRANSFER_OUT only) */}
          {(formData.transactionType === TransactionType.SELL) && (
            <div className="mb-4">
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Proceeds
              </label>
              <div className="flex items-end gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  name="proceeds"
                  value={formData.proceeds}
                  onChange={handleChange}
                  className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded flex-1 py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  disabled={loading}
                  placeholder="0.00"
                />
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-20 py-2 px-2 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline text-sm"
                  disabled={loading}
                  aria-label="Currency"
                >
                  {SUPPORTED_CURRENCIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Formula: Proceeds = Quantity × Price
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              Quantity
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="decimal"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className={`shadow appearance-none border ${
                  errors.quantity ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                } rounded w-full py-2 pr-12 pl-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline`}
                disabled={loading}
                placeholder="0.00"
              />
              {(() => {
                if (!showMaxQuantityButton) return null;
                const currentQty = formData.quantity || '0';
                if (compareDecimalStrings(currentQty, computedMaxQuantityString) < 0) {
                  return (
                    <button
                      type="button"
                      onClick={handleMaxQuantity}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                      disabled={loading}
                      title={`Set to Max (${computedMaxQuantityString})`}
                    >
                      Max
                    </button>
                  );
                }
                return null;
              })()}
            </div>
            {errors.quantity && (
              <p className="text-red-500 text-xs italic">{errors.quantity}</p>
            )}
          </div>

          {isInternalTransfer && (
            <div className="mb-4">
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Token fee
              </label>
              <input
                type="text"
                inputMode="decimal"
                name="feeQuantity"
                value={formData.feeQuantity}
                onChange={handleChange}
                className={`shadow appearance-none border ${errors.feeQuantity ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline`}
                disabled={loading}
                placeholder="0.00"
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Deducted from the source location in the same token, in addition to quantity.
              </div>
              {errors.feeQuantity && <p className="text-red-500 text-xs italic">{errors.feeQuantity}</p>}
            </div>
          )}

          {/* Price (hidden for transfers) */}
          {!isTransfer && (
            <div className="mb-4">
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Price
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className={`shadow appearance-none border ${
                    errors.price ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } rounded w-full py-2 pr-12 pl-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline`}
                  disabled={loading}
                  placeholder="0.00"
                />
                {(() => {
                  if (!currentPrice) return null;
                  const numericCurrentPrice = parseFloatFromString(currentPrice);
                  if (numericCurrentPrice <= 0) return null;
                  const currentPriceValue = parseFloatFromString(formData.price) || 0;
                  if (currentPriceValue === 0) {
                    return (
                      <button
                        type="button"
                        onClick={handleUseCurrentPrice}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                        disabled={loading}
                        title="Use current price"
                      >
                        Current
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
              {errors.price && (
                <p className="text-red-500 text-xs italic">{errors.price}</p>
              )}
            </div>
          )}

          {/* No separate currency block; currency is inline with cost/proceeds, and hidden for transfers */}

          {/* Timestamp */}
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              Date and Time
            </label>
            <input
              type="datetime-local"
              name="timestamp"
              value={formData.timestamp}
              onChange={handleChange}
              className="date-input shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              disabled={loading}
            />
          </div>

          {/* Optional Fields Section */}
          <div className="mb-3">
            <button
              type="button"
              onClick={() => setShowOptionalFields(!showOptionalFields)}
              className="flex items-center w-full py-1 px-0 text-left text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
            >
              <span className="font-medium">Fees & Notes (Optional)</span>
              <svg 
                className={`ml-auto h-4 w-4 transform transition-transform ${showOptionalFields ? 'rotate-180' : ''}`}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showOptionalFields && (
              <div className="mt-2 transition-all">
                {/* Fees */}
                <div className="mb-3">
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-1">
                    Fees
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    name="fees"
                    value={formData.fees}
                    onChange={handleChange}
                    className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    disabled={loading}
                    placeholder="0.00"
                  />
                </div>

                {/* Notes */}
                <div className="mb-3">
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    disabled={loading}
                    rows="2"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Error message */}
          {errors.submit && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
              {errors.submit}
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              {loading ? 'Saving...' : isEditing ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionModal; 